import { CheckCircle, Loader2, AlertCircle, Mail } from 'lucide-react';
import React from 'react';

import { useFeeSchedulesWithItems, type FeeScheduleWithItems } from '../../../hooks/useFeeSchedules';
import { useOnboardingStore } from '../../../store/onboarding.store';
import { calculatePreview, formatCents } from '../../../utils/feePreview';

interface FeeStructureStepProps {
  onNext: () => void;
  onBack: () => void;
}

const PREVIEW_BASE_CENTS = 10000; // $100

function ScheduleCard({
  schedule,
  isSelected,
  onSelect,
}: {
  schedule: FeeScheduleWithItems;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const hasItems = schedule.items.length > 0;

  if (!hasItems) {
    // Enterprise / empty schedule — show as disabled "Contact us" card
    return (
      <div
        className="w-full rounded-xl text-left overflow-hidden p-5 border-2 border-[#504A4A] bg-[#221F1F] opacity-70 cursor-not-allowed"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#504A4A] flex items-center justify-center">
            <Mail size={24} className="text-[#D1D5DB]" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-bold text-[#EBFFEE]">{schedule.name}</h3>
            </div>
            {schedule.description && (
              <p className="text-sm text-[#D1D5DB] mb-3">{schedule.description}</p>
            )}
            <p className="text-sm text-[#A9A3A3] italic">Contact us for custom pricing</p>
          </div>
        </div>
      </div>
    );
  }

  const preview = calculatePreview(schedule.items, PREVIEW_BASE_CENTS);

  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-xl text-left transition-all overflow-hidden p-5 ${
        isSelected
          ? 'ring-2 ring-[#9B1421] ring-offset-2 ring-offset-[#221F1F] bg-gradient-to-br from-[#9B1421]/20 via-[#221F1F] to-[#221F1F]'
          : 'border-2 border-[#504A4A] hover:border-[#9B1421]/50 bg-[#221F1F]'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-bold text-[#EBFFEE]">{schedule.name}</h3>
            {schedule.is_default && (
              <span className="text-xs bg-green-600/20 text-green-500 px-2 py-0.5 rounded-full">
                Recommended
              </span>
            )}
          </div>
          {schedule.description && (
            <p className="text-sm text-[#D1D5DB] mb-3">{schedule.description}</p>
          )}

          {/* Pricing Breakdown */}
          <div className="bg-[#111010] rounded-lg p-3 mt-3">
            <p className="text-xs text-[#D1D5DB] mb-2">
              Example: {formatCents(PREVIEW_BASE_CENTS)}/month subscription
            </p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-[#D1D5DB]">Subscription</span>
                <span className="text-[#EBFFEE]">{formatCents(PREVIEW_BASE_CENTS)}</span>
              </div>
              {preview.lineItems
                .filter(li => li.chargeEffect === 'add' && li.amountCents > 0)
                .map((li, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-[#D1D5DB]">{li.heading}</span>
                    <span className="text-yellow-500">+{formatCents(li.amountCents)}</span>
                  </div>
                ))}
              <div className="h-px bg-[#504A4A] my-2" />
              <div className="flex justify-between">
                <span className="text-[#EBFFEE] font-semibold">Customer Pays</span>
                <span className="text-[#EBFFEE] font-semibold">{formatCents(preview.payerCents)}</span>
              </div>
              <div className="h-px bg-[#504A4A] my-2" />
              <div className="flex justify-between">
                <span className="text-[#EBFFEE] font-semibold">Gym Receives</span>
                <span className="text-green-500 font-semibold">{formatCents(preview.gymReceives)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#D1D5DB] text-xs">Gym Fees</span>
                <span className="text-[#D1D5DB] text-xs">
                  {formatCents(PREVIEW_BASE_CENTS - preview.gymReceives)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {isSelected && (
          <div className="w-8 h-8 rounded-full bg-[#9B1421] flex items-center justify-center flex-shrink-0">
            <CheckCircle size={20} className="text-white" />
          </div>
        )}
      </div>
    </button>
  );
}

export function FeeStructureStep({ onNext, onBack }: FeeStructureStepProps) {
  const { billingData, setBillingData } = useOnboardingStore();
  const { data: schedules, isLoading, error } = useFeeSchedulesWithItems('customer_subscription');

  const handleSelect = (scheduleId: string) => {
    setBillingData({ feeScheduleId: scheduleId });
  };

  const handleContinue = () => {
    if (!billingData.feeScheduleId) return;
    onNext();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Your Fee Structure</h2>
          <p className="text-[#D1D5DB] mt-2">Loading available fee schedules...</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-rollops-accent" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Your Fee Structure</h2>
        </div>
        <div className="flex items-center gap-3 bg-red-900/20 border border-red-500/50 rounded-lg p-4">
          <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400">
            Failed to load fee schedules. Please try again.
          </p>
        </div>
        <div className="flex justify-between mt-8">
          <button
            onClick={onBack}
            className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Choose Your Fee Structure</h2>
        <p className="text-[#D1D5DB] mt-2">
          Select the fee schedule that works best for your gym
        </p>
      </div>

      <div className="space-y-4">
        {schedules && schedules.map((schedule) => (
          <ScheduleCard
            key={schedule.id}
            schedule={schedule}
            isSelected={billingData.feeScheduleId === schedule.id}
            onSelect={() => handleSelect(schedule.id)}
          />
        ))}
        {schedules && schedules.length === 0 && (
          <p className="text-[#A9A3A3] text-sm text-center py-8">
            No fee schedules are currently available. Please contact support.
          </p>
        )}
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-[#363232] text-[#EBFFEE] rounded-lg hover:bg-[#504A4A] transition-colors"
        >
          Go Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!billingData.feeScheduleId}
          className={`px-6 py-3 rounded-lg transition-colors ${
            billingData.feeScheduleId
              ? 'bg-[#9B1421] text-white hover:bg-[#C01F2D]'
              : 'bg-[#363232] text-[#D1D5DB] cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
